import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  DEFAULT_COMPANY_SETTINGS,
  type CompanyProfile,
  type CompanySettings,
  type CompanySettingsResponse,
  type PeriodCheckMode,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import CompanyBranding from './CompanyBranding';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  canEdit: boolean;
  onSaved: () => void;
  onSessionLost: () => void;
}

interface Option<T extends string> {
  value: T;
  label: string;
  hint: string;
}

/** اختيار واحد من متعدد — الموصى به (الافتراضى فى ADR-0006) يحمل علامته. */
function Choice<T extends string>({
  title,
  value,
  recommended,
  options,
  disabled,
  onChange,
}: {
  title: string;
  value: T;
  recommended: T;
  options: readonly Option<T>[];
  disabled: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="option-group" disabled={disabled}>
      <legend>{title}</legend>
      {options.map((option) => (
        <label key={option.value} className="option">
          <input
            type="radio"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>
            <strong>{option.label}</strong>
            {option.value === recommended && <span className="pill">موصى به</span>}
            <span className="label">{option.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function Toggle({
  label,
  hint,
  checked,
  recommended,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  recommended: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="option">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        <span className="pill">موصى به: {recommended ? 'مفعّل' : 'مطفأ'}</span>
        <span className="label">{hint}</span>
      </span>
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="panel">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

const CHECK_OPTIONS: readonly Option<PeriodCheckMode>[] = [
  { value: 'block', label: 'يمنع الإقفال', hint: 'لا يُقفل الشهر حتى تُحل المشكلة.' },
  { value: 'warn', label: 'تحذير فقط', hint: 'يظهر تنبيه، والإقفال مسموح بعد التأكيد.' },
  { value: 'off', label: 'لا يُفحص', hint: 'الإقفال بلا مراجعة لهذا البند.' },
];

/**
 * الإعدادات: بيانات الشركة وكل خيار محاسبى أو ضريبى اختاره المالك (ADR-0006).
 * الموصى به معلَّم، والتغيير لا يمس ما رُحّل — يحكم ما بعده فقط.
 */
export default function Settings({ companyId, canEdit, onSaved, onSessionLost }: Props) {
  const [loaded, setLoaded] = useState<CompanySettingsResponse | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setFailure(
          issues.length > 0
            ? issues.map((issue) => issue.message).join('، ')
            : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    api
      .companySettings(companyId)
      .then((data) => {
        setLoaded(data);
        setProfile(data.profile);
        setSettings(data.settings);
      })
      .catch(handleError);
  }, [companyId, handleError]);

  const disabled = !canEdit || saving;
  const D = DEFAULT_COMPANY_SETTINGS;

  function patch<K extends keyof CompanySettings>(
    key: K,
    value: Partial<CompanySettings[K]>,
  ): void {
    setSettings((current) => ({ ...current, [key]: { ...current[key], ...value } }));
    setSaved(false);
  }

  function patchProfile(value: Partial<CompanyProfile>): void {
    setProfile((current) => (current ? { ...current, ...value } : current));
    setSaved(false);
  }

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!profile) return;
    setSaving(true);
    setFailure(null);
    try {
      const result = await api.updateCompanySettings(companyId, { profile, settings });
      setLoaded(result);
      setProfile(result.profile);
      setSettings(result.settings);
      setSaved(true);
      onSaved();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSaving(false);
    }
  }

  if (!loaded || !profile) {
    return (
      <div className="panel">{failure ? <p className="error">{failure}</p> : <Loading />}</div>
    );
  }

  const nullable = (text: string) => (text.trim() === '' ? null : text);

  return (
    <form onSubmit={(event) => void save(event)} noValidate>
      {!canEdit && (
        <div className="panel">
          <p className="sub">
            تعرض الإعدادات للاطلاع — تغييرها للمالك أو من أُعطى صلاحية الإعدادات.
          </p>
        </div>
      )}

      <Section title="بيانات الشركة">
        <div className="form-row">
          <label className="grow">
            الاسم القانونى بالعربى
            <input
              value={profile.legalNameAr}
              maxLength={200}
              disabled={disabled}
              onChange={(event) => patchProfile({ legalNameAr: event.target.value })}
            />
            <Hint>كما هو مكتوب فى السجل التجارى بالضبط؛ يظهر أعلى الفواتير.</Hint>
          </label>
          <label className="grow">
            الاسم بالإنجليزى
            <input
              dir="ltr"
              value={profile.legalNameEn ?? ''}
              maxLength={200}
              disabled={disabled}
              onChange={(event) => patchProfile({ legalNameEn: nullable(event.target.value) })}
            />
            <Hint>اختيارى — يظهر فى المستندات بالإنجليزية.</Hint>
          </label>
        </div>
        <div className="form-row">
          <label className="grow">
            رقم التسجيل الضريبى (٩ أرقام)
            <input
              dir="ltr"
              inputMode="numeric"
              value={profile.taxRegistrationNumber ?? ''}
              maxLength={9}
              disabled={disabled}
              onChange={(event) =>
                patchProfile({ taxRegistrationNumber: nullable(event.target.value) })
              }
            />
            <Hint>الرقم المكتوب فى البطاقة الضريبية، ٩ أرقام بلا شرطات.</Hint>
          </label>
          <label className="grow">
            رقم السجل التجارى
            <input
              dir="ltr"
              value={profile.commercialRegistryNumber ?? ''}
              maxLength={32}
              disabled={disabled}
              onChange={(event) =>
                patchProfile({ commercialRegistryNumber: nullable(event.target.value) })
              }
            />
            <Hint>من شهادة السجل التجارى.</Hint>
          </label>
          <label>
            العملة
            <input value={loaded.functionalCurrency} disabled />
            <Hint>العملة التى تُسجَّل بها كل حسابات الشركة.</Hint>
          </label>
        </div>
      </Section>

      <CompanyBranding
        companyId={companyId}
        profile={profile}
        canEdit={canEdit}
        onSessionLost={onSessionLost}
      />

      <Section title="المبيعات">
        <Toggle
          label="الإشعار الدائن"
          hint="تصحيح فاتورة أو مرتجع جزئى بمستند مرتبط بها يقلل رصيد العميل."
          checked={settings.sales.creditNotes}
          recommended={D.sales.creditNotes}
          disabled={disabled}
          onChange={(creditNotes) => patch('sales', { creditNotes })}
        />
        <Toggle
          label="إلغاء الفاتورة كاملة"
          hint="زرار يلغى فاتورة لم يُحصَّل منها شىء بقيد عكسى."
          checked={settings.sales.invoiceCancellation}
          recommended={D.sales.invoiceCancellation}
          disabled={disabled}
          onChange={(invoiceCancellation) => patch('sales', { invoiceCancellation })}
        />
        <Toggle
          label="إلغاء إيصال التحصيل"
          hint="زرار يلغى إيصال اتسجل غلط بقيد عكسى بنفس تاريخه، وفواتيره ترجع مفتوحة."
          checked={settings.sales.receiptCancellation}
          recommended={D.sales.receiptCancellation}
          disabled={disabled}
          onChange={(receiptCancellation) => patch('sales', { receiptCancellation })}
        />
        <Toggle
          label="الإشعار المدين"
          hint="زيادة على فاتورة قديمة بدل فاتورة جديدة."
          checked={settings.sales.debitNotes}
          recommended={D.sales.debitNotes}
          disabled={disabled}
          onChange={(debitNotes) => patch('sales', { debitNotes })}
        />
        <Choice
          title="أين يُسجَّل الإشعار الدائن"
          value={settings.sales.creditNoteAccount}
          recommended={D.sales.creditNoteAccount}
          disabled={disabled}
          onChange={(creditNoteAccount) => patch('sales', { creditNoteAccount })}
          options={[
            {
              value: 'sales_returns',
              label: 'حساب مردودات ومسموحات المبيعات',
              hint: 'الإيراد الأصلى يبقى ظاهرًا، والتخفيضات فى سطر خاص بها فى قائمة الدخل.',
            },
            {
              value: 'original_revenue',
              label: 'عكس حساب إيراد السطر نفسه',
              hint: 'الإيراد يظهر صافيًا بعد التخفيض، بلا سطر مردودات منفصل.',
            },
          ]}
        />
        <Choice
          title="الدفعة المقدمة من العميل"
          value={settings.sales.customerAdvances}
          recommended={D.sales.customerAdvances}
          disabled={disabled}
          onChange={(customerAdvances) => patch('sales', { customerAdvances })}
          options={[
            {
              value: 'separate_account',
              label: 'حساب دفعات مقدمة منفصل',
              hint: 'التزام عليك حتى تُخصم من فاتورة — الميزانية أدق.',
            },
            {
              value: 'customer_balance',
              label: 'رصيد دائن على حساب العميل',
              hint: 'أبسط، لكن تقرير المديونيات يُظهر أرصدة بالسالب.',
            },
          ]}
        />
      </Section>

      <Section title="المشتريات">
        <Toggle
          label="الإشعار المدين للمورد (مرتجع أو خصم)"
          hint="مستند مرتبط بفاتورة المورد يقلل المستحق له — بدل تعديل الفاتورة."
          checked={settings.purchases.debitNotes}
          recommended={D.purchases.debitNotes}
          disabled={disabled}
          onChange={(debitNotes) => patch('purchases', { debitNotes })}
        />
        <Toggle
          label="الإشعار الدائن من المورد"
          hint="زيادة يرسلها المورد على فاتورة قديمة بدل فاتورة جديدة."
          checked={settings.purchases.creditNotes}
          recommended={D.purchases.creditNotes}
          disabled={disabled}
          onChange={(creditNotes) => patch('purchases', { creditNotes })}
        />
        <Toggle
          label="إلغاء فاتورة المورد كاملة"
          hint="زرار يلغى فاتورة مورد لم يُسدد منها شىء بقيد عكسى."
          checked={settings.purchases.invoiceCancellation}
          recommended={D.purchases.invoiceCancellation}
          disabled={disabled}
          onChange={(invoiceCancellation) => patch('purchases', { invoiceCancellation })}
        />
        <Toggle
          label="إلغاء سند السداد"
          hint="زرار يلغى سداد لمورد اتسجل غلط بقيد عكسى بنفس تاريخه، وفواتيره ترجع مفتوحة."
          checked={settings.purchases.paymentCancellation}
          recommended={D.purchases.paymentCancellation}
          disabled={disabled}
          onChange={(paymentCancellation) => patch('purchases', { paymentCancellation })}
        />
        <Choice
          title="أين يُسجَّل الإشعار المدين للمورد"
          value={settings.purchases.debitNoteAccount}
          recommended={D.purchases.debitNoteAccount}
          disabled={disabled}
          onChange={(debitNoteAccount) => patch('purchases', { debitNoteAccount })}
          options={[
            {
              value: 'purchase_returns',
              label: 'حساب مردودات ومسموحات المشتريات',
              hint: 'المصروف الأصلى يبقى ظاهرًا، والمرتجعات فى سطر خاص بها. مرتجع الأصول يُعكس على حسابه دائمًا.',
            },
            {
              value: 'original_account',
              label: 'عكس حساب سطر الفاتورة نفسه',
              hint: 'المصروف يظهر صافيًا بعد المرتجع، بلا سطر مردودات منفصل.',
            },
          ]}
        />
      </Section>

      <Section title="المستندات والخزينة">
        <Choice
          title="ترقيم المستندات"
          value={settings.documents.numbering}
          recommended={D.documents.numbering}
          disabled={disabled}
          onChange={(numbering) => patch('documents', { numbering })}
          options={[
            {
              value: 'prefix_year_sequence',
              label: 'INV-2026-000001',
              hint: 'بادئة وسنة ومسلسل يبدأ من جديد كل سنة.',
            },
            {
              value: 'continuous',
              label: 'INV-000001',
              hint: 'مسلسل مستمر لا يرجع لواحد أبدًا.',
            },
          ]}
        />
        <Toggle
          label="فتح حساب أستاذ تلقائيًا لخزينة أو بنك جديد"
          hint="أول ما تضيف خزينة أو بنك، يُفتح لها حساب فرعى تحت النقدية أو البنوك."
          checked={settings.treasury.autoCreateLedgerAccount}
          recommended={D.treasury.autoCreateLedgerAccount}
          disabled={disabled}
          onChange={(autoCreateLedgerAccount) => patch('treasury', { autoCreateLedgerAccount })}
        />
      </Section>

      <Section title="المخزون">
        <Toggle
          label="تفعيل المخزون"
          hint="لشركات تبيع بضاعة: أصناف مخزنية بكمياتها وتكلفتها، وتكلفة المبيعات تُرحَّل مع الفاتورة. شركة خدمات لا تحتاجه."
          checked={settings.inventory.enabled}
          recommended={D.inventory.enabled}
          disabled={disabled}
          onChange={(enabled) => patch('inventory', { enabled })}
        />
        {settings.inventory.enabled && (
          <>
            <Choice
              title="طريقة تكلفة المنصرف (تنتظر اعتماد المحاسب)"
              value={settings.inventory.costMethod}
              recommended={D.inventory.costMethod}
              disabled={disabled}
              onChange={(costMethod) => patch('inventory', { costMethod })}
              options={[
                {
                  value: 'weighted_average',
                  label: 'المتوسط المرجح',
                  hint: 'كل وارد يعدّل متوسط تكلفة الوحدة، والصرف بالمتوسط الحالى.',
                },
                {
                  value: 'fifo',
                  label: 'الوارد أولاً يُصرف أولاً (FIFO)',
                  hint: 'الصرف من أقدم دفعة شراء فما بعدها بتكلفتها.',
                },
              ]}
            />
            <Toggle
              label="السماح بالبيع بلا رصيد كافٍ"
              hint="الرصيد يصير سالبًا ويُسعَّر العجز بآخر تكلفة شراء. الأدق تركه مطفأ."
              checked={settings.inventory.allowNegativeStock}
              recommended={D.inventory.allowNegativeStock}
              disabled={disabled}
              onChange={(allowNegativeStock) => patch('inventory', { allowNegativeStock })}
            />
          </>
        )}
      </Section>

      <Section title="الأرصدة الافتتاحية والإقفال">
        <Choice
          title="فرق الأرصدة الافتتاحية غير المتوازنة"
          value={settings.opening.difference}
          recommended={D.opening.difference}
          disabled={disabled}
          onChange={(difference) => patch('opening', { difference })}
          options={[
            {
              value: 'suspense_account',
              label: 'حساب «فروق افتتاحية» مؤقت',
              hint: 'الفرق يُحفظ مؤقتًا، ويجب أن يصير صفرًا قبل إقفال أول فترة.',
            },
            { value: 'reject', label: 'يُرفض حتى تتوازن', hint: 'أدق، لكن يعطّل البداية.' },
            { value: 'equity', label: 'على رأس المال', hint: 'الفرق يُحسب حقوق ملكية دائمًا.' },
          ]}
        />
        <Choice
          title="إقفال السنة المالية"
          value={settings.closing.yearEnd}
          recommended={D.closing.yearEnd}
          disabled={disabled}
          onChange={(yearEnd) => patch('closing', { yearEnd })}
          options={[
            {
              value: 'auto_entry',
              label: 'قيد إقفال تلقائى',
              hint: 'يرحّل صافى الربح أو الخسارة للأرباح المحتجزة بعد تأكيد المالك.',
            },
            { value: 'manual', label: 'المحاسب يعمله يدويًا', hint: 'قيد يومية عادى.' },
            {
              value: 'reports_only',
              label: 'تقارير فقط بلا قيد',
              hint: 'التقارير تحسب المرحّل وقت العرض.',
            },
          ]}
        />
        <Choice
          title="قبل إقفال الفترة: بنك غير مطابق حتى نهايتها"
          value={settings.closing.periodChecks.bankReconciled}
          recommended={D.closing.periodChecks.bankReconciled}
          disabled={disabled}
          onChange={(bankReconciled) =>
            patch('closing', {
              periodChecks: { ...settings.closing.periodChecks, bankReconciled },
            })
          }
          options={CHECK_OPTIONS}
        />
        <Choice
          title="قبل إقفال الفترة: فرق أرصدة افتتاحية قائم"
          value={settings.closing.periodChecks.openingDifference}
          recommended={D.closing.periodChecks.openingDifference}
          disabled={disabled}
          onChange={(openingDifference) =>
            patch('closing', {
              periodChecks: { ...settings.closing.periodChecks, openingDifference },
            })
          }
          options={CHECK_OPTIONS}
        />
      </Section>

      <Section title="ترحيل البيانات من برنامج سابق">
        <div className="form-row">
          <label>
            تاريخ بداية العمل على البرنامج
            <input
              type="date"
              value={settings.migration.cutoverDate ?? ''}
              disabled={disabled}
              onChange={(event) => patch('migration', { cutoverDate: event.target.value || null })}
            />
            <Hint>اليوم الذى تبدأ فيه التسجيل على هذا البرنامج. ما قبله يدخل كأرصدة افتتاحية.</Hint>
          </label>
          <label>
            حد تشابه الأسماء (٪)
            <input
              type="number"
              min={60}
              max={99}
              value={settings.migration.similarityPercent}
              disabled={disabled}
              onChange={(event) =>
                patch('migration', {
                  similarityPercent: Math.min(99, Math.max(60, Number(event.target.value) || 85)),
                })
              }
            />
            <Hint>
              عند ترحيل بياناتك القديمة، الأسماء المتشابهة بهذه النسبة أو أكثر تُعرض عليك كتكرار
              محتمل لتقرر.
            </Hint>
          </label>
        </div>
        <Choice
          title="أسماء متقاربة فى الملفات المرحَّلة (شركة النور / شركه النور)"
          value={settings.migration.similarNames}
          recommended={D.migration.similarNames}
          disabled={disabled}
          onChange={(similarNames) => patch('migration', { similarNames })}
          options={[
            {
              value: 'warn',
              label: 'تنبيه يُراجع',
              hint: 'يظهر بجوار الصف مع زر دمج، ولا يمنع الاعتماد.',
            },
            {
              value: 'block',
              label: 'يمنع الاعتماد',
              hint: 'حتى يُدمج الاسمان أو يُؤكَّد أنهما مختلفان.',
            },
            { value: 'off', label: 'لا يُفحص', hint: 'المكرر بالاسم نفسه يُكشف على أى حال.' },
          ]}
        />
        <Choice
          title="رصيد حساب ضريبى فى الميزان المرحَّل (القيمة المضافة، الخصم من المنبع)"
          value={settings.migration.taxBalances}
          recommended={D.migration.taxBalances}
          disabled={disabled}
          onChange={(taxBalances) => patch('migration', { taxBalances })}
          options={[
            {
              value: 'carry_without_tax_event',
              label: 'يُنقل رصيدًا بلا حدث ضريبى',
              hint: 'لا يظهر فى إقرار فترة الترحيل لأنه ليس معاملة فيها. يراجعه مستشار الضرائب.',
            },
            { value: 'reject', label: 'يُرفض', hint: 'حتى يُوجَّه رصيده لحساب آخر.' },
          ]}
        />
        <Toggle
          label="مساعدة الذكاء الاصطناعى فى المطابقة (المرحلة القادمة)"
          hint="موافقة الشركة على إرسال عينات من ملفاتها لمساعد يقترح مطابقة الأعمدة والحسابات. لا يُرسل شىء حاليًا أيًّا كان الاختيار."
          checked={settings.migration.aiAssist}
          recommended={D.migration.aiAssist}
          disabled={disabled}
          onChange={(aiAssist) => patch('migration', { aiAssist })}
        />
      </Section>

      <Section title="التقارير">
        <Choice
          title="النقدية فى قائمة التدفقات النقدية"
          value={settings.reports.cashFlow.cashScope}
          recommended={D.reports.cashFlow.cashScope}
          disabled={disabled}
          onChange={(cashScope) =>
            patch('reports', { cashFlow: { ...settings.reports.cashFlow, cashScope } })
          }
          options={[
            {
              value: 'cash_group',
              label: 'كل النقدية وما فى حكمها',
              hint: 'كما يعرّفها المعيار: الخزائن والبنوك ومعها العهد المستديمة والنقدية بالطريق والودائع قصيرة الأجل.',
            },
            {
              value: 'cash_subledgers',
              label: 'الخزائن والبنوك والمحافظ فقط',
              hint: 'نفس أرقام حركة النقدية واللوحة. العهد والودائع القصيرة تُعامل كأصل متداول.',
            },
          ]}
        />
      </Section>

      <Section title="الضرائب">
        <Choice
          title="ضريبة القيمة المضافة"
          value={settings.tax.vatRegistration}
          recommended={D.tax.vatRegistration}
          disabled={disabled}
          onChange={(vatRegistration) => patch('tax', { vatRegistration })}
          options={[
            {
              value: 'unknown',
              label: 'لم يُحدد بعد',
              hint: 'لا ضريبة على الفواتير حتى يُحدد التسجيل وتُعتمد النسبة.',
            },
            {
              value: 'registered',
              label: 'مسجلة',
              hint: 'النسبة العامة 14% وأى نسبة أخرى تُطبق بعد اعتماد المالك.',
            },
            { value: 'not_registered', label: 'غير مسجلة', hint: 'لا ضريبة على الفواتير.' },
          ]}
        />
        <Toggle
          label="الخصم والإضافة: ما يخصمه العملاء منا"
          hint="الإيصال يسجّل المخصوم رصيدًا لك لدى المصلحة بدل أن يبقى على العميل."
          checked={settings.tax.withholding.onReceipts}
          recommended={D.tax.withholding.onReceipts}
          disabled={disabled}
          onChange={(onReceipts) =>
            patch('tax', { withholding: { ...settings.tax.withholding, onReceipts } })
          }
        />
        {settings.tax.withholding.onReceipts && (
          <label className="grow">
            <span>نسب الخصم المقترحة فى شاشة التحصيل (%)</span>
            {/* النسب القانونية يحددها مستشار الضرائب، لا البرنامج — القائمة فارغة حتى تُدخل */}
            <input
              key={settings.tax.withholding.receiptRates.join(',')}
              defaultValue={settings.tax.withholding.receiptRates.join('، ')}
              placeholder="مثال: 1، 3 — بعد مراجعة مستشار الضرائب"
              disabled={disabled}
              onBlur={(event) => {
                const receiptRates = [
                  ...new Set(
                    event.target.value
                      .split(/[,،\s]+/)
                      .map((value) => value.trim())
                      .filter((value) => /^\d{1,3}(\.\d{1,4})?$/.test(value)),
                  ),
                ].slice(0, 20);
                patch('tax', { withholding: { ...settings.tax.withholding, receiptRates } });
              }}
            />
            <span className="label">
              اقتراح للاختيار السريع فقط؛ تبقى النسبة قابلة للكتابة يدويًا فى كل إيصال.
            </span>
            <Hint>أزرار سريعة تظهر فى شاشة تحصيل العملاء لتختار نسبة الخصم بضغطة.</Hint>
          </label>
        )}
        <Toggle
          label="الخصم والإضافة: ما نخصمه من الموردين"
          hint="لو الشركة ملزمة بالخصم من مورديها وتوريده."
          checked={settings.tax.withholding.onPayments}
          recommended={D.tax.withholding.onPayments}
          disabled={disabled}
          onChange={(onPayments) =>
            patch('tax', { withholding: { ...settings.tax.withholding, onPayments } })
          }
        />
        {settings.tax.withholding.onPayments && (
          <label className="grow">
            <span>نسب الخصم المقترحة فى شاشة السداد (%)</span>
            {/* النسب القانونية يحددها مستشار الضرائب، لا البرنامج — القائمة فارغة حتى تُدخل */}
            <input
              key={settings.tax.withholding.paymentRates.join(',')}
              defaultValue={settings.tax.withholding.paymentRates.join('، ')}
              placeholder="بعد مراجعة مستشار الضرائب"
              disabled={disabled}
              onBlur={(event) => {
                const paymentRates = [
                  ...new Set(
                    event.target.value
                      .split(/[,،\s]+/)
                      .map((value) => value.trim())
                      .filter((value) => /^\d{1,3}(\.\d{1,4})?$/.test(value)),
                  ),
                ].slice(0, 20);
                patch('tax', { withholding: { ...settings.tax.withholding, paymentRates } });
              }}
            />
            <span className="label">
              اقتراح للاختيار السريع فقط؛ تبقى النسبة قابلة للكتابة يدويًا فى كل سداد.
            </span>
            <Hint>أزرار سريعة تظهر فى شاشة سداد الموردين لتختار نسبة الخصم بضغطة.</Hint>
          </label>
        )}
        <Toggle
          label="إرسال الفاتورة الإلكترونية للمنظومة"
          hint="يُفعَّل بعد التسجيل فى المنظومة وتوفر بيانات اختبار رسمية واعتماد مستشار الضرائب."
          checked={settings.tax.eInvoice.enabled}
          recommended={D.tax.eInvoice.enabled}
          disabled={disabled}
          onChange={(enabled) => patch('tax', { eInvoice: { enabled } })}
        />
      </Section>

      {canEdit && (
        <div className="panel">
          {failure && <p className="error">{failure}</p>}
          {saved && <p className="sub">حُفظت الإعدادات.</p>}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'جارى الحفظ…' : 'حفظ الإعدادات'}
            </button>
            <button
              type="button"
              className="ghost"
              disabled={saving}
              onClick={() => {
                setSettings(DEFAULT_COMPANY_SETTINGS);
                setSaved(false);
              }}
            >
              الرجوع للموصى به
            </button>
            <span className="label">
              التغيير يحكم ما يُسجَّل بعده فقط — المستندات المرحّلة لا تتغير.
            </span>
          </div>
        </div>
      )}
    </form>
  );
}
